import {
  BuyerNotification,
  ListResult,
  NotificationQuery,
  NotificationType,
} from '../types';
import { delay } from './delay';
import { ERR_NOT_FOUND } from './errors';

const minutesAgo = (m: number) => new Date(Date.now() - m * 60 * 1000).toISOString();
const hoursAgo = (h: number) => new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
const daysAgo = (d: number) => new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString();

const seed: BuyerNotification[] = [
  {
    id: 'ntf-001',
    type: 'BUSINESS_INVITATION_RECEIVED',
    payload: {
      businessName: 'XYZ Medical Centre',
      invitedBy: 'Sarah Lee',
      invitationToken: 'inv-token-james',
    },
    createdAt: minutesAgo(12),
  },
  {
    id: 'ntf-002',
    type: 'ORDER_DISPATCHED',
    payload: {
      orderId: 'ord-1042',
      orderNumber: 'VM-2026-1042',
      carrier: 'Australia Post',
      trackingNumber: 'AP-2026-554321',
    },
    createdAt: hoursAgo(2),
  },
  {
    id: 'ntf-003',
    type: 'VIRTUAL_PRODUCT_DELIVERED',
    payload: {
      orderId: 'ord-1040',
      orderNumber: 'VM-2026-1040',
      productName: 'TCM Diagnostics Masterclass — On-demand',
    },
    createdAt: hoursAgo(4),
  },
  {
    id: 'ntf-004',
    type: 'ORDER_PAID',
    payload: { orderId: 'ord-1041', orderNumber: 'VM-2026-1041', amount: 412.5 },
    readAt: hoursAgo(5),
    createdAt: hoursAgo(6),
  },
  {
    id: 'ntf-005',
    type: 'VIRTUAL_PRODUCT_DELIVERY_FAILED',
    payload: {
      orderId: 'ord-1038',
      orderNumber: 'VM-2026-1038',
      productName: 'Sydney Clinic-Day Workshop — May 2026',
      reason: 'Recipient mailbox temporarily unavailable. Retry scheduled.',
    },
    createdAt: hoursAgo(9),
  },
  {
    id: 'ntf-006',
    type: 'ORDER_PAYMENT_FAILED',
    payload: {
      orderId: 'ord-1037',
      orderNumber: 'VM-2026-1037',
      reason: 'Card declined by issuer.',
    },
    readAt: daysAgo(1),
    createdAt: daysAgo(1),
  },
  {
    id: 'ntf-007',
    type: 'BUSINESS_INVITATION_ACCEPTED',
    payload: { businessName: 'XYZ Medical Centre', invitedBy: 'Mira Patel' },
    readAt: daysAgo(2),
    createdAt: daysAgo(2),
  },
  {
    id: 'ntf-008',
    type: 'VIRTUAL_PRODUCT_AUTO_DELISTED',
    payload: { productName: 'Winter CPD Workshop 2026' },
    readAt: daysAgo(3),
    createdAt: daysAgo(3),
  },
  {
    id: 'ntf-009',
    type: 'ORDER_EXPIRED',
    payload: { orderId: 'ord-1029', orderNumber: 'VM-2026-1029' },
    readAt: daysAgo(5),
    createdAt: daysAgo(5),
  },
  {
    id: 'ntf-010',
    type: 'BUSINESS_REGISTERED',
    payload: { businessName: 'XYZ Medical Centre' },
    readAt: daysAgo(14),
    createdAt: daysAgo(14),
  },
];

const store: BuyerNotification[] = seed.map((n) => ({ ...n }));

const matchesQuery = (n: BuyerNotification, q: NotificationQuery): boolean => {
  if (q.unreadOnly && n.readAt) return false;
  return true;
};

export const notificationMock = {
  list: async (
    query: NotificationQuery = {},
  ): Promise<ListResult<BuyerNotification>> => {
    await delay();
    const skip = query.skip ?? 0;
    const take = query.take ?? 0;
    const filtered = store.filter((n) => matchesQuery(n, query));
    const items = take === 0 ? filtered.slice(skip) : filtered.slice(skip, skip + take);
    return { items: items.map((n) => ({ ...n })), skip, take, total: filtered.length };
  },

  unreadCount: async (): Promise<number> => {
    await delay();
    return store.filter((n) => !n.readAt).length;
  },

  markRead: async (id: string): Promise<BuyerNotification> => {
    await delay();
    const n = store.find((x) => x.id === id);
    if (!n) throw ERR_NOT_FOUND('Notification');
    if (!n.readAt) n.readAt = new Date().toISOString();
    return { ...n };
  },

  markAllRead: async (): Promise<void> => {
    await delay();
    const now = new Date().toISOString();
    store.forEach((n) => {
      if (!n.readAt) n.readAt = now;
    });
  },

  // Dev helper: push a new notification (not exposed via service).
  push: (type: NotificationType, payload?: BuyerNotification['payload']): void => {
    store.unshift({
      id: `ntf-${Date.now()}`,
      type,
      payload,
      createdAt: new Date().toISOString(),
    });
  },
};
